import { useQuery } from "@tanstack/react-query";
import { useEffect } from "react";

import { api } from "../api/rest";
import { useOpsAlerts } from "../store/useOpsAlerts";

/**
 * Как часто перечитывать состояние тревог.
 *
 * Пятнадцать секунд — период опроса самого движка: чаще спрашивать нечего, он
 * всё равно не узнает нового.
 */
const OPS_REFETCH_MS = 15_000;

/**
 * Поднятие тревог, висящих на момент открытия вкладки, и страховка от разрыва.
 *
 * Сокет отдаёт только то, что случилось ПОСЛЕ подключения, поэтому тревога,
 * поднятая ночью, иначе не показалась бы вовсе — при том, что стек всё ещё
 * сломан.
 *
 * # Почему опрос идёт и в ФОНОВОЙ вкладке
 *
 * В отличие от карточек замка (там refetchIntervalInBackground выключен
 * сознательно — торговое предложение живёт минуты и в фоне бесполезно), здесь
 * опрос ведётся всегда. Поломка стека не перестаёт быть поломкой оттого, что
 * оператор переключился на другую вкладку, и звонок — единственный способ
 * позвать его обратно. Тот же довод, что у панели статуса.
 *
 * Возвращает ТРИ состояния, а не два:
 *
 *   failed — запрос не прошёл: web_api не отвечает, и о тревогах мы не знаем
 *            ничего (в том числе и того, что их нет);
 *   silent — подсистема выключена (OPS_ENABLED);
 *   иначе  — список честен, и пустота означает «поломок сейчас нет».
 *
 * Второе и третье выглядят на экране одинаковой пустотой, а означают разное:
 * первое — решение оператора, второе — то, чего и ждут.
 */
export function useOpsAlertsFeed(): { failed: boolean; known: boolean } {
  const ingestMany = useOpsAlerts((s) => s.ingestMany);

  const query = useQuery({
    queryKey: ["ops", "alerts"],
    queryFn: () => api.opsAlerts(),
    refetchInterval: OPS_REFETCH_MS,
    refetchIntervalInBackground: true,
    retry: 1,
  });

  useEffect(() => {
    if (!query.data) return;
    // И висящие, и недавние: вернувшийся к столу оператор обязан увидеть, что
    // тревога БЫЛА, даже если она уже снялась сама.
    ingestMany([...(query.data.firing ?? []), ...(query.data.recent ?? [])]);
  }, [query.data, ingestMany]);

  return {
    failed: query.isError,
    known: query.data?.known ?? false,
  };
}
